import React from 'react';

/**
 * Utility to retry dynamic imports (React.lazy) when they fail, 
 * typically due to a ChunkLoadError during/after deployments.
 * 
 * @param {Function} componentImport - The () => import() function
 * @param {number} retriesLeft - Number of retries before giving up
 * @param {number} interval - Interval between retries in ms
 * @returns {Promise} - The resolved component
 */
export const lazyRetry = (componentImport, retriesLeft = 2, interval = 1000) => {
    return new Promise((resolve, reject) => {
        componentImport()
            .then(resolve)
            .catch((error) => {
                if (retriesLeft <= 0) {
                    console.error('All retries for lazy component failed:', error);

                    // After a deployment, old chunk files no longer exist on the server.
                    // The server returns index.html instead, causing "Unexpected token '<'".
                    // Force a full page reload to pick up the new chunk manifest,
                    // but use a sessionStorage flag to avoid infinite reload loops.
                    const isChunkError =
                        error?.name === 'ChunkLoadError' ||
                        error?.message?.includes('Loading chunk') ||
                        error?.message?.includes('Unexpected token');

                    const reloadKey = 'chunk_reload_attempted';
                    const alreadyReloaded = sessionStorage.getItem(reloadKey);

                    if (isChunkError && !alreadyReloaded) {
                        console.warn('ChunkLoadError detected after retries. Forcing full page reload...');
                        sessionStorage.setItem(reloadKey, 'true');
                        window.location.reload();
                        return;
                    }

                    // Clear the flag on next successful load (handled below) or give up
                    reject(error);
                    return;
                }

                console.warn(`Lazy load failed. Retrying in ${interval}ms... (${retriesLeft} retries left)`, error);

                setTimeout(() => {
                    lazyRetry(componentImport, retriesLeft - 1, interval).then(resolve, reject);
                }, interval);
            });
    });
};

// Clear the reload flag on successful app boot so future deployments can trigger it again
try {
    sessionStorage.removeItem('chunk_reload_attempted');
} catch (_) {
    // sessionStorage may not be available in some environments
}
